import { useMemo, useState } from "react";

import SemverService from "@services/semverService/semverService";

import { ReleaseFilesState } from "./useReleaseFiles";

const enrollmentReleaseChannels = new Set<API.ReleaseFile["channel"]>(["beta", "live"]);

interface SelectOption {
    label: string;
    value: string;
}

interface UseProductVersionSelectionOptions {
    autoSelect?: boolean;
    releaseFiles: ReleaseFilesState;
}

interface UseProductVersionSelectionReturn {
    releaseFiles: ReleaseFilesState;
    selectedProductCodename: string;
    setSelectedProductCodename: (codename: string) => void;
    selectedVersion: string;
    setSelectedVersion: (version: string) => void;
    productOptions: SelectOption[];
    versionOptions: SelectOption[];
    selectedFile: API.ReleaseFile | undefined;
}

export const useEnrollmentProductSelection = (options: UseProductVersionSelectionOptions): UseProductVersionSelectionReturn => {
    const { autoSelect = true, releaseFiles } = options;
    const [chosenProductCodename, setChosenProductCodename] = useState<string>("");
    const [chosenVersion, setChosenVersion] = useState<{ codename: string; version: string } | null>(null);

    const enrollmentReleaseFiles = useMemo(
        () => releaseFiles.data.filter((file) => enrollmentReleaseChannels.has(file.channel)),
        [releaseFiles.data],
    );

    const productOptions = useMemo(() => {
        const uniqueCodenames = [...new Set(enrollmentReleaseFiles.map((file) => file.codename))];
        return uniqueCodenames.map((codename) => {
            const firstFile = enrollmentReleaseFiles.find((file) => file.codename === codename);
            return {
                label: firstFile?.productName || codename,
                value: codename,
            };
        });
    }, [enrollmentReleaseFiles]);

    const selectedProductCodename = chosenProductCodename || (autoSelect ? (productOptions[0]?.value ?? "") : "");

    const versionOptions = useMemo(() => {
        if (!selectedProductCodename) {
            return [];
        }
        const versions = [
            ...new Set(
                enrollmentReleaseFiles.filter((file) => file.codename === selectedProductCodename).map((file) => file.version),
            ),
        ];
        return versions
            .sort((a, b) => SemverService.compare(b, a))
            .map((version) => ({
                label: version,
                value: version,
            }));
    }, [enrollmentReleaseFiles, selectedProductCodename]);

    const selectedVersion =
        chosenVersion?.codename === selectedProductCodename ? chosenVersion.version : (versionOptions[0]?.value ?? "");

    const selectedFile = useMemo(() => {
        if (!selectedProductCodename || !selectedVersion) {
            return undefined;
        }
        return enrollmentReleaseFiles.find(
            (file) => file.codename === selectedProductCodename && file.version === selectedVersion,
        );
    }, [enrollmentReleaseFiles, selectedProductCodename, selectedVersion]);

    return {
        releaseFiles,
        selectedProductCodename,
        setSelectedProductCodename: setChosenProductCodename,
        selectedVersion,
        setSelectedVersion: (version: string) => setChosenVersion({ codename: selectedProductCodename, version }),
        productOptions,
        versionOptions,
        selectedFile,
    };
};
